import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { IVehicle } from "../../models/Vehicle";
import LeadParts from "../../components/leads/LeadParts/LeadParts";
import PartVehicleDetails from "../../components/leads/LeadParts/PartVehicleDetails";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { useForm } from "react-hook-form";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useNotification from "../../components/notifications/useNotifications";
import { isStoredVehicle } from "../../utils/vehicleDisplay";

export default function PartsVehicleLookup() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { register, getValues} = useForm<any>();
  const { vrmLookup, vrmLookupData } = useVehiclesMutations();
  const [isLoading, setIsLoading] = useState(false);
  const [vehicle, setVehicle] = useState<IVehicle | null>(null);


  // Looking a reg up is not the same as taking the car in. This fetches the details without
  // writing anything, because a vehicle created here has no lead — and a document hangs off a
  // lead, so it would be a vehicle no photograph could ever be attached to.
  const handleVrmLookup = () => {
    setIsLoading(true);
    vrmLookupData.mutate(getValues("vrm"), {
      onSuccess: (response) => {
        if (!response?.data) {
          addNotification({
            variant: "error",
            primaryText: "No vehicle found for that registration",
          });
          setVehicle(null);
          return;
        }

        setVehicle(response.data);
      },
      onError: (error) => {
        console.error(error);
        addNotification({
          variant: "error",
          primaryText: "Error looking up VRM",
        });
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  }

  // Parts and jobs are keyed on a vehicle id, so they need a real record. Adding one is a
  // deliberate step rather than a side effect of the search.
  const handleAddToWilma = () => {
    if (!vehicle?.vrm) return;

    setIsLoading(true);
    vrmLookup.mutate(vehicle.vrm, {
      onSuccess: (response) => setVehicle(response.data),
      onError: (error) => {
        console.error(error);
        addNotification({
          variant: "error",
          primaryText: "Error adding vehicle",
        });
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  }

  useEffect(() => {
    setInfo({
      name: "Parts Vehicle Lookup",
      desc: "Lookup details and part information for a vehicle",
    });
  }, []);

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div className="mt-4">
      <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
        <TextInput label="VRM" register={register} registerName="vrm" />
        <div className="flex items-center mt-7">
          <ButtonInput label="Find Vehicle" isSubmit={false} classes="w-40 h-10" onClick={() => handleVrmLookup()} />
        </div>
      </div>

      {vehicle && !isStoredVehicle(vehicle) &&
        <>
          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <p>
              These are DVLA details — this vehicle isn't in Wilma yet, so it has no parts or jobs.
            </p>
            <div className="mt-2">
              <ButtonInput
                label="Add Vehicle To Wilma"
                isSubmit={false}
                classes="w-52 h-10"
                onClick={() => handleAddToWilma()}
              />
            </div>
          </div>

          <div className="mt-6">
            <PartVehicleDetails vehicle={vehicle} price={null} collectionQuestions />
          </div>
        </>
      }

      {/* Parts and job creation key off the vehicle id, so they only make sense once we hold it. */}
      {vehicle && isStoredVehicle(vehicle) &&
        <LeadParts lead={null} vehicle={vehicle} />
      }
    </div>
  )
}
